import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  DiscussionError,
  deleteComment,
  editComment,
  setCitationNeeded,
  postComment,
  reportComment,
  setCommentHidden,
  type CommentAnchor,
  type DiscussionComment,
  type DiscussionKind
} from '@peruselab/api-client/discussions';
import { Icon } from '../../components/Icon';
import { relativeTime } from '../../components/panels/MyDrawingsFeed';
import { CommentComposer } from './CommentComposer';
import { ReferenceList } from './ReferenceCitation';
import { countReplies, linkify } from './discussionTree';

interface CommentItemProps {
  readonly comment: DiscussionComment;
  readonly depth: number;
  readonly maxDepth: number;
  readonly kind: DiscussionKind;
  readonly artifactId: string;
  readonly canComment: boolean;
  readonly signedIn: boolean;
  readonly moderator: boolean;
  readonly describeAnchor?: ((anchor: CommentAnchor) => string) | undefined;
  /** Shows what an anchor points at (the workspace selects it on the paper); absent elsewhere. */
  readonly onShowAnchor?: ((anchor: CommentAnchor) => void) | undefined;
  readonly onChanged: () => Promise<void>;
  readonly onContinue: (commentId: string) => void;
}

export function defaultAnchorLabel(anchor: CommentAnchor, t: TFunction): string {
  switch (anchor.kind) {
    case 'property':
      return t('discussions.anchor.property', { property: anchor.property });
    case 'assertion':
      return t('discussions.anchor.assertion', { label: anchor.label });
    case 'statement':
      return t('discussions.anchor.statement', {
        quote: anchor.quote.length > 90 ? `${anchor.quote.slice(0, 89)}…` : anchor.quote,
        source: anchor.source
      });
    case 'atom':
      return t('discussions.anchor.atom');
    case 'atom-set':
      return t('discussions.anchor.atoms', { count: anchor.atomIds.length });
    case 'bond':
      return t('discussions.anchor.bond');
    case 'bond-set':
      return t('discussions.anchor.bonds', { count: anchor.bondIds.length });
  }
}

/** One comment and, recursively, its replies (ADR 0016 D4). */
export function CommentItem(props: CommentItemProps) {
  const { comment, depth, maxDepth, kind, artifactId, canComment, signedIn, moderator, describeAnchor, onShowAnchor } =
    props;
  const { t, i18n } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [mode, setMode] = useState<'idle' | 'replying' | 'editing'>('idle');
  const [notice, setNotice] = useState<string | null>(null);
  const replies = countReplies(comment);

  const act = async (action: () => Promise<void>, done?: string) => {
    setNotice(null);
    try {
      await action();
      if (done) setNotice(done);
      await props.onChanged();
    } catch (error) {
      setNotice(t(`discussions.errors.${error instanceof DiscussionError ? error.kind : 'unknown'}`));
    }
  };

  return (
    <li className={`comment${comment.hidden ? ' comment--hidden' : ''}`} data-testid="comment">
      <div className="comment-header">
        <button
          type="button"
          className="icon-button comment-collapse"
          aria-expanded={!collapsed}
          aria-label={collapsed ? t('discussions.expand') : t('discussions.collapse')}
          title={collapsed ? t('discussions.expand') : t('discussions.collapse')}
          onClick={() => setCollapsed((value) => !value)}
        >
          <Icon name={collapsed ? 'chevronRight' : 'expandMore'} size="1.1rem" />
        </button>
        <span className="comment-author">
          {comment.deleted ? t('discussions.deletedAuthor') : comment.authorName || t('publicPages.unknownAuthor')}
        </span>
        <span className="comment-meta">
          {relativeTime(comment.createdAt, i18n.language)}
          {comment.editedAt ? ` · ${t('discussions.edited')}` : ''}
        </span>
        {/* ADR 0016 D21 (#910): what supports it -- shown, never hidden; no badge is an opinion or a question. */}
        {comment.footing === 'SOURCED' ? (
          <span className="comment-badge comment-badge--sourced">
            <Icon name="link" size="0.85rem" />
            <span>{t('discussions.footing.sourced')}</span>
          </span>
        ) : comment.footing === 'DATA' ? (
          <span className="comment-badge comment-badge--data">
            <Icon name="monitoring" size="0.85rem" />
            <span>{t('discussions.footing.data')}</span>
          </span>
        ) : null}
        {comment.references.length > 0 ? (
          <span className="comment-meta">{t('discussions.footing.cites', { count: comment.references.length })}</span>
        ) : null}
        {comment.hidden ? <span className="comment-badge">{t('discussions.hiddenBadge')}</span> : null}
        {moderator && comment.reportCount ? (
          <span className="comment-badge comment-badge--warning">
            {t('discussions.reports', { count: comment.reportCount })}
          </span>
        ) : null}
        {collapsed && replies > 0 ? (
          <span className="comment-meta">{t('discussions.collapsedReplies', { count: replies })}</span>
        ) : null}
      </div>
      {collapsed ? null : (
        <>
          {comment.anchor ? (
            <p className={`comment-anchor${comment.anchorOutdated ? ' comment-anchor--outdated' : ''}`}>
              {comment.anchorOutdated ? (
                t('discussions.anchor.outdated')
              ) : onShowAnchor && comment.anchor.kind !== 'property' ? (
                // A tap, not a hover, so it works the same on touch (multitouch-safe NFR).
                <button
                  type="button"
                  className="discussion-link-button"
                  title={t('discussions.anchor.show')}
                  onClick={() => onShowAnchor(comment.anchor!)}
                >
                  {describeAnchor?.(comment.anchor) ?? defaultAnchorLabel(comment.anchor, t)}
                </button>
              ) : (
                (describeAnchor?.(comment.anchor) ?? defaultAnchorLabel(comment.anchor, t))
              )}
            </p>
          ) : null}
          {mode === 'editing' ? (
            <CommentComposer
              initial={comment.body ?? ''}
              submitLabel={t('discussions.save')}
              initialReferences={comment.references}
              onSubmit={async (body, references) => {
                await editComment(
                  comment.id,
                  body,
                  references.map((reference) => reference.id)
                );
                setMode('idle');
                await props.onChanged();
              }}
              onCancel={() => setMode('idle')}
            />
          ) : comment.deleted ? (
            <p className="comment-body comment-body--deleted">{t('discussions.deleted')}</p>
          ) : (
            <p className="comment-body" dir="auto">
              {linkify(comment.body ?? '').map((piece, index) =>
                'href' in piece ? (
                  <a key={index} href={piece.href} target="_blank" rel="noopener noreferrer nofollow ugc">
                    {piece.href}
                  </a>
                ) : (
                  <span key={index}>{piece.text}</span>
                )
              )}
            </p>
          )}
          {comment.deleted || mode === 'editing' ? null : <ReferenceList references={comment.references} />}
          {comment.deleted || mode === 'editing' ? null : (
            <div className="comment-actions">
              {canComment ? (
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('discussions.reply')}
                  title={t('discussions.reply')}
                  onClick={() => setMode('replying')}
                >
                  <Icon name="reply" size="1.1rem" />
                </button>
              ) : null}
              {comment.mine && canComment ? (
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('discussions.edit')}
                  title={t('discussions.edit')}
                  onClick={() => setMode('editing')}
                >
                  <Icon name="edit" size="1.1rem" />
                </button>
              ) : null}
              {comment.mine ? (
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('discussions.delete')}
                  title={t('discussions.delete')}
                  onClick={() => {
                    if (globalThis.confirm(t('discussions.confirmDelete'))) void act(() => deleteComment(comment.id));
                  }}
                >
                  <Icon name="delete" size="1.1rem" />
                </button>
              ) : null}
              {moderator && !comment.mine ? (
                <button
                  type="button"
                  className="icon-button"
                  aria-label={comment.hidden ? t('discussions.unhide') : t('discussions.hide')}
                  title={comment.hidden ? t('discussions.unhide') : t('discussions.hide')}
                  onClick={() => void act(() => setCommentHidden(comment.id, !comment.hidden))}
                >
                  <Icon name={comment.hidden ? 'visibility' : 'visibilityOff'} size="1.1rem" />
                </button>
              ) : null}
              {/* D21: "citation needed" -- for others' comments that don't cite a source yet; the count shows to all. */}
              {comment.footing === null && (comment.citationNeeded > 0 || (signedIn && !comment.mine)) ? (
                <button
                  type="button"
                  className={`citation-needed${comment.citationNeededByMe ? ' citation-needed--mine' : ''}`}
                  aria-pressed={signedIn && !comment.mine ? comment.citationNeededByMe : undefined}
                  disabled={!signedIn || comment.mine}
                  title={
                    comment.mine
                      ? t('discussions.citationNeeded.yours')
                      : comment.citationNeededByMe
                        ? t('discussions.citationNeeded.withdraw')
                        : t('discussions.citationNeeded.flag')
                  }
                  onClick={() => void act(() => setCitationNeeded(comment.id, !comment.citationNeededByMe))}
                >
                  {t('discussions.citationNeeded.label')}
                  {comment.citationNeeded > 0 ? (
                    <span className="citation-needed-count">{comment.citationNeeded}</span>
                  ) : null}
                </button>
              ) : null}
              {signedIn && !comment.mine && !comment.hidden ? (
                <button
                  type="button"
                  className="icon-button"
                  aria-label={t('discussions.report')}
                  title={t('discussions.report')}
                  onClick={() => {
                    if (globalThis.confirm(t('discussions.confirmReport'))) {
                      void act(() => reportComment(comment.id), t('discussions.reported'));
                    }
                  }}
                >
                  <Icon name="flag" size="1.1rem" />
                </button>
              ) : null}
            </div>
          )}
          {notice ? (
            <p className="comment-notice" role="status">
              {notice}
            </p>
          ) : null}
          {mode === 'replying' ? (
            <CommentComposer
              submitLabel={t('discussions.reply')}
              onSubmit={async (body, references) => {
                await postComment(kind, artifactId, {
                  body,
                  parentId: comment.id,
                  references: references.map((reference) => reference.id)
                });
                setMode('idle');
                await props.onChanged();
              }}
              onCancel={() => setMode('idle')}
            />
          ) : null}
          {comment.replies.length > 0 ? (
            depth >= maxDepth ? (
              <button type="button" className="discussion-link-button" onClick={() => props.onContinue(comment.id)}>
                {t('discussions.continueThread', { count: replies })}
              </button>
            ) : (
              <ol className="discussion-comments discussion-replies">
                {comment.replies.map((reply) => (
                  <CommentItem key={reply.id} {...props} comment={reply} depth={depth + 1} />
                ))}
              </ol>
            )
          ) : null}
        </>
      )}
    </li>
  );
}
